import { useState } from 'react'
import {
  usePackages,
  useCreatePackage,
  useUpdatePackage,
  useTogglePackage,
  useDeletePackage,
} from '@/hooks/usePlans'
import { DataTable } from '@/components/common/DataTable'
import { StatusBadge } from '@/components/common/StatusBadge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Package, CreatePackageRequest } from '@/types'
import { formatCurrency, getApiErrorMessage } from '@/lib/utils'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { ConfirmDialog } from '@/components/admin/ConfirmDialog'
import { toast } from 'sonner'

const emptyForm: CreatePackageRequest = {
  name: '',
  description: '',
  document_count: 10,
  price_usd: 0,
  sort_order: 0,
}

export const PackagesListPage = () => {
  const { data: packages, isLoading } = usePackages()
  const createPackage = useCreatePackage()
  const updatePackage = useUpdatePackage()
  const togglePackage = useTogglePackage()
  const deletePackage = useDeletePackage()

  const [createOpen, setCreateOpen] = useState(false)
  const [editPkg, setEditPkg] = useState<Package | null>(null)
  const [deletePkg, setDeletePkg] = useState<Package | null>(null)
  const [form, setForm] = useState<CreatePackageRequest>(emptyForm)

  const openCreate = () => {
    setForm(emptyForm)
    setCreateOpen(true)
  }

  const openEdit = (pkg: Package) => {
    setForm({
      name: pkg.name,
      description: pkg.description,
      document_count: pkg.document_count,
      price_usd: pkg.price_usd,
      sort_order: pkg.sort_order,
    })
    setEditPkg(pkg)
  }

  const handleCreate = async () => {
    try {
      await createPackage.mutateAsync(form)
      setCreateOpen(false)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Error al crear paquete'))
    }
  }

  const handleEdit = async () => {
    if (!editPkg) return
    try {
      await updatePackage.mutateAsync({ id: editPkg.id, updates: form })
      setEditPkg(null)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Error al actualizar paquete'))
    }
  }

  const columns = [
    {
      key: 'name',
      header: 'Nombre',
      cell: (pkg: Package) => (
        <div>
          <p className="font-medium">{pkg.name}</p>
          <p className="text-xs text-muted-foreground">{pkg.description}</p>
        </div>
      ),
    },
    {
      key: 'document_count',
      header: 'Documentos',
      cell: (pkg: Package) => <span className="font-medium">{pkg.document_count}</span>,
    },
    {
      key: 'price_usd',
      header: 'Precio',
      cell: (pkg: Package) => <span className="font-medium">{formatCurrency(pkg.price_usd)}</span>,
    },
    {
      key: 'purchase_count',
      header: 'Compras',
      cell: (pkg: Package) => <span>{pkg.purchase_count ?? 0}</span>,
    },
    {
      key: 'revenue_total',
      header: 'Ingresos',
      cell: (pkg: Package) => (
        <span className="text-sm">{formatCurrency(pkg.revenue_total ?? 0)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Estado',
      cell: (pkg: Package) => <StatusBadge status={pkg.is_active ? 'active' : 'inactive'} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      cell: (pkg: Package) => (
        <div className="flex items-center justify-end gap-2">
          <Switch checked={pkg.is_active} onCheckedChange={() => togglePackage.mutate(pkg.id)} />
          <Button variant="ghost" size="sm" onClick={() => openEdit(pkg)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setDeletePkg(pkg)}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ]

  const formDialog = (
    <div className="grid gap-4 py-4">
      <div className="grid gap-2">
        <Label>Nombre</Label>
        <Input
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        />
      </div>
      <div className="grid gap-2">
        <Label>Descripcion</Label>
        <Input
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label>Documentos</Label>
          <Input
            type="number"
            min={1}
            value={form.document_count}
            onChange={(e) => setForm((f) => ({ ...f, document_count: Number(e.target.value) }))}
          />
        </div>
        <div className="grid gap-2">
          <Label>Precio (USD)</Label>
          <Input
            type="number"
            min={0}
            step={0.01}
            value={form.price_usd}
            onChange={(e) => setForm((f) => ({ ...f, price_usd: Number(e.target.value) }))}
          />
        </div>
      </div>
      <div className="grid gap-2">
        <Label>Orden</Label>
        <Input
          type="number"
          min={0}
          value={form.sort_order}
          onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) }))}
        />
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Paquetes</h1>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Crear paquete
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={packages ?? []}
        isLoading={isLoading}
        keyExtractor={(p) => p.id}
        emptyMessage="No hay paquetes"
      />

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear paquete</DialogTitle>
          </DialogHeader>
          {formDialog}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={createPackage.isPending}>
              {createPackage.isPending ? 'Creando...' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editPkg} onOpenChange={(open) => !open && setEditPkg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar paquete: {editPkg?.name}</DialogTitle>
          </DialogHeader>
          {formDialog}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPkg(null)}>
              Cancelar
            </Button>
            <Button onClick={handleEdit} disabled={updatePackage.isPending}>
              {updatePackage.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      {deletePkg && (
        <ConfirmDialog
          open={!!deletePkg}
          onOpenChange={(open) => !open && setDeletePkg(null)}
          title="Eliminar paquete"
          description={`¿Eliminar el paquete "${deletePkg.name}"? Esta accion no se puede deshacer.`}
          confirmLabel="Eliminar"
          variant="destructive"
          onConfirm={() => deletePackage.mutateAsync(deletePkg.id)}
          isLoading={deletePackage.isPending}
        />
      )}
    </div>
  )
}
